import { useRef } from "react";
import {
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

import {
  ACCEPTED_ATTACHMENT_LABEL,
  ACCEPTED_FILE_INPUT_ACCEPT,
  thinScrollbarSx,
} from "./constants";

type ComposerProps = {
  draft: string;
  onDraftChange: (value: string) => void;
  files: File[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  onSend: () => void;
  sending: boolean;
  hasConversation: boolean;
  placeholder: string;
  error?: string | null;
  inputRef: React.RefObject<HTMLInputElement>;
};

const Composer = ({
  draft,
  onDraftChange,
  files,
  onAddFiles,
  onRemoveFile,
  onSend,
  sending,
  hasConversation,
  placeholder,
  error,
  inputRef,
}: ComposerProps) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Stack spacing={1} sx={{ flexShrink: 0 }}>
      {error && (
        <Alert severity="error" sx={{ py: 0 }}>
          {error}
        </Alert>
      )}

      {files.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {files.map((file, index) => (
            <Chip
              key={`${file.name}-${index}`}
              size="small"
              icon={<InsertDriveFileOutlinedIcon />}
              label={file.name}
              onDelete={() => onRemoveFile(index)}
            />
          ))}
        </Stack>
      )}

      <TextField
        inputRef={inputRef}
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
          }
        }}
        disabled={sending}
        placeholder={hasConversation ? undefined : `${placeholder}...`}
        multiline
        minRows={1}
        maxRows={6}
        fullWidth
        sx={{
          "& .MuiInputBase-root": {
            borderRadius: 3,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.05)
                : alpha(theme.palette.common.black, 0.03),
            py: 0.75,
            px: 1,
            fontSize: "0.95rem",
            alignItems: "center",
          },
          "& .MuiInputBase-input": {
            py: 0.75,
            lineHeight: 1.5,
            overflowY: "auto",
            ...thinScrollbarSx,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "divider",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5 }}>
              <Tooltip title={`Anexar arquivo (${ACCEPTED_ATTACHMENT_LABEL})`}>
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                >
                  <AttachFileOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept={ACCEPTED_FILE_INPUT_ACCEPT}
                onChange={(e) => {
                  if (e.target.files?.length) {
                    onAddFiles(Array.from(e.target.files));
                  }
                  e.target.value = "";
                }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                color="primary"
                onClick={onSend}
                disabled={(!draft.trim() && files.length === 0) || sending}
                sx={{
                  bgcolor:
                    draft.trim() || files.length > 0
                      ? alpha(theme.palette.primary.main, 0.12)
                      : "transparent",
                }}
              >
                <SendRoundedIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  );
};

export default Composer;
